import pluralize from 'pluralize';

import {
  EllipsisWithTooltip,
  Icons,
  ListItem,
  ListItemAvatar,
  ListItemText,
  withProps,
  Box,
  tokens,
} from '@cast/design-system';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { Organization } from '@cast/types';

import { OrganizationBadge } from './OrganizationBadge';

const OrganizationListItemText = withProps(ListItemText, {
  primaryTypographyProps: { variant: 'P12M', component: 'span' },
  secondaryTypographyProps: { variant: 'P10R' },
});

type EnterpriseListItemProps = {
  organization: Organization;
  organizationsCount: number;
  isEscalation?: boolean;
  isBlocked?: boolean;
  onSelect: (organization: Organization) => void;
};

export const EnterpriseListItem = ({
  organization,
  organizationsCount,
  isEscalation,
  isBlocked,
  onSelect,
}: EnterpriseListItemProps) => {
  const secondaryText = `Enterprise | ${organizationsCount} ${pluralize(
    'child organization',
    organizationsCount
  )}`;

  if (!organization?.id || !organization?.name) {
    return null;
  }

  return (
    <ListItem
      testId="existing-enterprise"
      key={organization.id}
      startAdornment={
        <ListItemAvatar sx={{ overflow: 'visible' }}>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              opacity: isBlocked ? 0.4 : 1,
            }}
          >
            <OrganizationAvatar size="md" organizationId={organization.id} />
            {organization.organizationMember === false && (
              <OrganizationBadge
                isEscalation={isEscalation}
                isBlocked={isBlocked}
              />
            )}
          </Box>
        </ListItemAvatar>
      }
      endAdornment={
        <Box
          sx={{
            height: '16px',
            '&:hover': {
              color: tokens.theme.fg.action.primary,
            },
          }}
        >
          <Icons.CaretRight size="16px" />
        </Box>
      }
      multiline
      onClick={isBlocked ? undefined : () => onSelect(organization)}
      sx={{ cursor: isBlocked ? 'default' : 'pointer' }}
      role="menuitem"
      aria-label={`Select ${organization.name} enterprise with ${organizationsCount} child organizations`}
      aria-disabled={isBlocked}
    >
      <OrganizationListItemText
        primary={
          <EllipsisWithTooltip
            sx={{
              width: '162px',
              color: isBlocked ? tokens.theme.disabled.fg : undefined,
            }}
          >
            {organization.name}
          </EllipsisWithTooltip>
        }
        secondary={secondaryText}
      />
    </ListItem>
  );
};

type DefaultOrganizationListItemProps = {
  organization: Organization;
  isActive: boolean;
  isEscalation?: boolean;
  isBlocked?: boolean;
  onSelect: (organization: Organization) => void;
};

export const DefaultOrganizationListItem = ({
  organization,
  isActive,
  isEscalation,
  isBlocked,
  onSelect,
}: DefaultOrganizationListItemProps) => {
  if (!organization?.id || !organization?.name) {
    return null;
  }

  return (
    <ListItem
      testId="existing-organization"
      key={organization.id}
      startAdornment={
        <ListItemAvatar sx={{ overflow: 'visible' }}>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              opacity: isBlocked ? 0.4 : 1,
            }}
          >
            <OrganizationAvatar size="md" organizationId={organization.id} />
            {organization.organizationMember === false && (
              <OrganizationBadge
                isEscalation={isEscalation}
                isBlocked={isBlocked}
              />
            )}
          </Box>
        </ListItemAvatar>
      }
      multiline
      onClick={isBlocked ? undefined : () => onSelect(organization)}
      sx={{
        backgroundColor: isActive
          ? tokens.theme.bg.selection.primarySubtle
          : 'none',
        cursor: isBlocked ? 'default' : 'pointer',
      }}
      role="menuitem"
      aria-label={`Select ${organization.name} organization${
        isActive ? ' (currently active)' : ''
      }`}
      aria-disabled={isBlocked}
    >
      <OrganizationListItemText
        primary={
          <EllipsisWithTooltip
            sx={{
              width: '186px',
              color: isBlocked ? tokens.theme.disabled.fg : undefined,
            }}
          >
            {organization.name}
          </EllipsisWithTooltip>
        }
      />
    </ListItem>
  );
};

type ChildOrganizationListItemProps = {
  organization: Organization;
  isActive: boolean;
  isBlocked?: boolean;
  onSelect: (organization: Organization) => void;
};

export const ChildOrganizationListItem = ({
  organization,
  isActive,
  isBlocked,
  onSelect,
}: ChildOrganizationListItemProps) => {
  if (!organization?.id || !organization?.name) {
    return null;
  }

  return (
    <ListItem
      testId="existing-child-organization"
      key={organization.id}
      startAdornment={
        <ListItemAvatar sx={{ overflow: 'visible' }}>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              opacity: isBlocked ? 0.4 : 1,
            }}
          >
            <OrganizationAvatar size="2xs" organizationId={organization.id} />
            {organization.organizationMember === false && (
              <OrganizationBadge isEscalation isBlocked={isBlocked} />
            )}
          </Box>
        </ListItemAvatar>
      }
      multiline
      onClick={isBlocked ? undefined : () => onSelect(organization)}
      sx={{
        backgroundColor: isActive
          ? tokens.theme.bg.selection.primarySubtle
          : 'none',
      }}
      role="menuitem"
      aria-label={`Select ${organization.name} child organization${
        isActive ? ' (currently active)' : ''
      }`}
      aria-disabled={isBlocked}
    >
      <OrganizationListItemText
        primary={
          <EllipsisWithTooltip
            sx={{
              width: '198px',
              color: isBlocked ? tokens.theme.disabled.fg : undefined,
            }}
          >
            {organization.name}
          </EllipsisWithTooltip>
        }
      />
    </ListItem>
  );
};
